import {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react';
import { Flex, Avatar, Icon } from '@chakra-ui/react';
import { getTheWordObj } from '@/pages/interviews/utils/transcript';
import { Play } from '@kula-ai/icons/dual-tone/Play';
import { KText } from '@/ui-library/components/text';

interface Word {
  text: string;
  start_timestamp: number;
}

interface Speech {
  speaker: string;
  words: Word[];
}

interface TranscriptData {
  transcript?: Speech[];
}

interface TranscriptProps {
  currentTime?: number;
  data: TranscriptData;
  handleTimeJump: (_time: number, _shouldPlay?: boolean) => void;
  playerType?: 'mini' | 'modal';
}

interface CurrentIndex {
  sentence: number;
  word: number;
}

interface TranscriptRef {
  triggerFunction: () => void;
}

export const Transcript = forwardRef<TranscriptRef, TranscriptProps>(
  ({ currentTime = 0, data, handleTimeJump, playerType = 'mini' }, ref) => {
    const { transcript } = data || {};
    const [currentIndex, setCurrentIndex] = useState<CurrentIndex>({
      sentence: 0,
      word: 0,
    });
    const [previousIndex, setPreviousIndex] = useState<CurrentIndex | null>(
      null
    );
    const containerRef = useRef<HTMLDivElement>(null);
    const stateRef = useRef({
      scrollCounter: 0,
      prevTime: currentTime,
      isTimerActive: false,
      scrollDebounceTimer: null as NodeJS.Timeout | null,
    });

    const handleScroll = () => {
      // Only count as manual scroll if timer isn't active (no recent time jump)
      if (!stateRef.current.isTimerActive) {
        stateRef.current.scrollCounter = 1;

        // Clear previous debounce timer
        if (stateRef.current.scrollDebounceTimer) {
          clearTimeout(stateRef.current.scrollDebounceTimer);
        }

        // Reset scroll counter after user stops scrolling for 1 second
        stateRef.current.scrollDebounceTimer = setTimeout(() => {
          stateRef.current.scrollCounter = 0;
        }, 1000);
      }
    };

    useEffect(() => {
      // Store reference to cleanup timers
      const debounceTimer = stateRef.current.scrollDebounceTimer;

      // Delay to ensure element is rendered in DOM, especially after tab switches
      const timeout = setTimeout(() => {
        const elementId = `word-${playerType}-${currentIndex?.sentence}-${currentIndex?.word}`;
        const element = document.getElementById(elementId);

        if (element) {
          element.style.background = '#605AFB';
          element.style.borderRadius = '3px';
          element.style.color = 'white';

          const shouldAutoScroll = stateRef.current.scrollCounter === 0;

          // Only auto-scroll if counter = 0 (user is just watching, not reading/scrolling)
          if (shouldAutoScroll) {
            // Activate timer to prevent scroll events from this animation from blocking auto-scroll
            stateRef.current.isTimerActive = true;

            // Scroll the container directly
            if (containerRef.current) {
              const elementRect = element.getBoundingClientRect();
              const containerRect =
                containerRef.current.getBoundingClientRect();
              const scrollTop = containerRef.current.scrollTop;
              const offset = elementRect.top - containerRect.top + scrollTop;

              containerRef.current.scrollTo({
                top: offset - containerRect.height / 2,
                behavior: 'smooth',
              });

              // Deactivate timer after scroll animation completes
              setTimeout(() => {
                stateRef.current.isTimerActive = false;
              }, 800);
            } else {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });

              // Deactivate timer after scroll animation completes
              setTimeout(() => {
                stateRef.current.isTimerActive = false;
              }, 800);
            }
          }
        }
      }, 50);

      if (previousIndex !== null) {
        const previousElement = document.getElementById(
          `word-${playerType}-${previousIndex?.sentence}-${previousIndex?.word}`
        );
        if (previousElement) {
          previousElement.style.background = 'inherit';
          previousElement.style.color = 'inherit';
        }
      }
      setPreviousIndex(currentIndex);

      return () => {
        clearTimeout(timeout);
        if (debounceTimer) {
          clearTimeout(debounceTimer);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex, playerType]);

    useEffect(() => {
      if (transcript) {
        const obj = getTheWordObj(transcript, currentTime);
        setCurrentIndex(obj?.indexes);

        // Reset counter on any intentional time jump (> 50ms)
        // This detects user seeks/clicks on progress bar
        const timeDiff = Math.abs(currentTime - stateRef.current.prevTime);

        if (timeDiff > 50) {
          stateRef.current.scrollCounter = 0;
          // Mark timer as active to prevent scroll listener from blocking auto-scroll
          stateRef.current.isTimerActive = true;

          setTimeout(() => {
            stateRef.current.isTimerActive = false;
          }, 800);
        }
        stateRef.current.prevTime = currentTime;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTime]);

    const handleCopy = () => {
      const formattedContent = transcript
        ?.map(
          (speech) =>
            `${speech?.speaker}: ${speech?.words
              .map((word) => `${word?.text} `)
              .join('')}`
        )
        .join('\n\n');

      navigator.clipboard
        .writeText(formattedContent)
        .then(() => {
          console.log('Text copied!!');
        })
        .catch((err) => {
          console.error('Failed to copy text: ', err);
        });
    };

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        if (stateRef.current.scrollDebounceTimer) {
          //eslint-disable-next-line
          clearTimeout(stateRef.current.scrollDebounceTimer);
        }
      };
    }, []);

    // In this case, it allows the parent component to call the 'handleCopy' function, which copies the transcript text to the clipboard.
    // This is useful for providing a way for the parent component to trigger the copy action without needing to directly manage the state or logic of the child component.
    useImperativeHandle(ref, () => ({
      triggerFunction: handleCopy,
    }));

    return (
      <Flex
        ref={containerRef}
        flexDirection="column"
        flex={1}
        overflowY="auto"
        mt="20px"
        pb="20px"
        onScroll={handleScroll}
      >
        {transcript?.map((speech, index) => (
          <Flex
            key={'speech' + index}
            mb="20px"
            pos="relative"
            alignItems="center"
            _hover={{ '> div': { visibility: 'visible', opacity: 1 } }}
          >
            <Flex>
              <Avatar
                name={speech?.speaker}
                size="xs"
                mr="12px"
                borderRadius="md"
              />
              <Flex flexDirection="column">
                <KText size="sm-semi-bold" mb="5px" color="secondary-900">
                  {speech?.speaker}
                </KText>
                <Flex wrap={'wrap'}>
                  {speech?.words.map((word, wordIndex) => (
                    <KText
                      mr="3px"
                      size="sm-regular"
                      id={`word-${playerType}-${index}-${wordIndex}`}
                      key={`word-${index}-${wordIndex}`}
                    >
                      {word?.text}
                    </KText>
                  ))}
                </Flex>
              </Flex>
            </Flex>
            <Flex
              bg="rgba(96, 90, 251, 0.16);"
              w="26px"
              h="26px"
              borderRadius="50%"
              alignItems="center"
              justifyContent="center"
              pos="absolute"
              right="0"
              cursor="pointer"
              visibility="hidden"
              onClick={() =>
                handleTimeJump(speech?.words?.[0]?.start_timestamp * 1000, true)
              }
            >
              <Icon as={Play} w="12px" h="12px" color="#605AFB" />
            </Flex>
          </Flex>
        ))}
      </Flex>
    );
  }
);
